import React from "react";
import { useSearchParams } from "react-router-dom";
import { parseGridUrlState, serializeGridUrlState, clearPrefixedParams } from "./index.js";
import {
  applyPresetStateToGrid,
  extractPresetStateFromGrid,
  buildPresetParamKey,
  getPresetIdFromUrl,
  hasManualGridParams,
} from "../dataGridPresets/index.js";

const URL_UPDATE_DEBOUNCE_MS = 300;

/**
 * @param {number} defaultPageSize
 * @returns {ReturnType<typeof parseGridUrlState>}
 */
function createEmptyParsed(defaultPageSize) {
  return {
    filterItems: [],
    quickFilterValue: "",
    sortModel: [],
    paginationModel: { page: 0, pageSize: defaultPageSize },
    columnVisibilityModel: {},
  };
}

/**
 * @param {Object} parsed
 * @returns {import('@mui/x-data-grid').GridFilterModel}
 */
function buildFilterModel(parsed) {
  return {
    items: parsed.filterItems,
    quickFilterValues: parsed.quickFilterValue ? [parsed.quickFilterValue] : [],
  };
}

/**
 * @param {Object} options
 * @param {string} [options.urlStateKey]
 * @param {number} options.defaultPageSize
 * @param {Object} [options.initialState]
 * @param {string[]} [options.columnFields]
 * @param {Object} [options.presetsBinding]
 * @param {boolean} [options.presetsBinding.enabled]
 * @param {boolean} [options.presetsBinding.loaded]
 * @param {Object[]} [options.presetsBinding.presets]
 * @param {Function} [options.onFilterModelChange]
 * @param {Function} [options.onSortModelChange]
 * @param {Function} [options.onPaginationModelChange]
 * @param {Function} [options.onColumnVisibilityModelChange]
 */
export function useDataGridUrlState({
  urlStateKey,
  defaultPageSize,
  initialState,
  columnFields,
  presetsBinding,
  onFilterModelChange: onFilterModelChangeProp,
  onSortModelChange: onSortModelChangeProp,
  onPaginationModelChange: onPaginationModelChangeProp,
  onColumnVisibilityModelChange: onColumnVisibilityModelChangeProp,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const presetsEnabled = Boolean(presetsBinding?.enabled);

  const defaultColumnVisibility = React.useMemo(
    () => initialState?.columns?.columnVisibilityModel ?? {},
    [initialState],
  );

  const codecOptions = React.useMemo(
    () => ({
      defaultPageSize,
      defaultColumnVisibility,
      allowedFields: columnFields,
    }),
    [defaultPageSize, defaultColumnVisibility, columnFields],
  );

  const parseFromUrl = React.useCallback(() => {
    if (!urlStateKey) {
      return createEmptyParsed(defaultPageSize);
    }
    return parseGridUrlState(searchParams, urlStateKey, codecOptions);
  }, [searchParams, urlStateKey, codecOptions, defaultPageSize]);

  const buildStateFromParsed = React.useCallback(
    (parsed) => ({
      filterModel: buildFilterModel(parsed),
      sortModel: parsed.sortModel,
      paginationModel: parsed.paginationModel,
      columnVisibilityModel: parsed.columnVisibilityModel,
    }),
    [],
  );

  const initialHasManualParams = Boolean(urlStateKey && hasManualGridParams(searchParams, urlStateKey));
  const initialParsed = React.useMemo(
    () => (initialHasManualParams ? parseFromUrl() : createEmptyParsed(defaultPageSize)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const initialBuilt = React.useMemo(() => buildStateFromParsed(initialParsed), [buildStateFromParsed, initialParsed]);

  const [filterModel, setFilterModel] = React.useState(initialBuilt.filterModel);
  const [sortModel, setSortModel] = React.useState(initialBuilt.sortModel);
  const [paginationModel, setPaginationModel] = React.useState(initialBuilt.paginationModel);
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState(initialBuilt.columnVisibilityModel);
  const [activePresetId, setActivePresetId] = React.useState(() => {
    if (!urlStateKey || initialHasManualParams) {
      return null;
    }
    return getPresetIdFromUrl(searchParams, urlStateKey);
  });

  const debounceRef = React.useRef(null);
  const skipExternalSyncRef = React.useRef(false);
  const suppressManualUrlRef = React.useRef(false);
  const activePresetIdRef = React.useRef(activePresetId);
  activePresetIdRef.current = activePresetId;
  const presetBootstrapRef = React.useRef(
    Boolean(initialHasManualParams || getPresetIdFromUrl(searchParams, urlStateKey ?? "")),
  );

  const currentState = React.useMemo(
    () => ({
      filterModel,
      sortModel,
      paginationModel,
      columnVisibilityModel,
    }),
    [filterModel, sortModel, paginationModel, columnVisibilityModel],
  );

  const stateRef = React.useRef(currentState);
  stateRef.current = currentState;

  const writePresetUrl = React.useCallback(
    (presetId) => {
      if (!urlStateKey) {
        return;
      }
      skipExternalSyncRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          clearPrefixedParams(next, urlStateKey);
          if (presetId) {
            next.set(buildPresetParamKey(urlStateKey), presetId);
          }
          return next;
        },
        { replace: true },
      );
    },
    [urlStateKey, setSearchParams],
  );

  const writeManualUrlFromState = React.useCallback(
    (nextState) => {
      if (!urlStateKey) {
        return;
      }
      const serialized = serializeGridUrlState(nextState, urlStateKey, codecOptions);
      skipExternalSyncRef.current = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          clearPrefixedParams(next, urlStateKey);
          for (const [key, value] of Object.entries(serialized)) {
            next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [urlStateKey, codecOptions, setSearchParams],
  );

  const scheduleManualUrlUpdate = React.useCallback(
    (nextState) => {
      if (!urlStateKey || suppressManualUrlRef.current) {
        return;
      }
      setActivePresetId(null);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        writeManualUrlFromState(nextState);
      }, URL_UPDATE_DEBOUNCE_MS);
    },
    [urlStateKey, writeManualUrlFromState],
  );

  const shouldTreatGridChangeAsManual = React.useCallback(() => {
    if (suppressManualUrlRef.current || !urlStateKey || presetBootstrapRef.current) {
      return false;
    }
    return true;
  }, [urlStateKey]);

  const finishPresetBootstrap = React.useCallback(() => {
    queueMicrotask(() => {
      suppressManualUrlRef.current = false;
      presetBootstrapRef.current = false;
    });
  }, []);

  const applyPreset = React.useCallback(
    (preset, writeUrl = true) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      suppressManualUrlRef.current = true;
      presetBootstrapRef.current = true;
      const nextBuilt = applyPresetStateToGrid(preset.state, defaultPageSize, { allowedFields: columnFields });
      setFilterModel(nextBuilt.filterModel);
      setSortModel(nextBuilt.sortModel);
      setPaginationModel(nextBuilt.paginationModel);
      setColumnVisibilityModel(nextBuilt.columnVisibilityModel);
      setActivePresetId(String(preset._id));
      if (writeUrl) {
        writePresetUrl(String(preset._id));
      }
      finishPresetBootstrap();
    },
    [defaultPageSize, columnFields, writePresetUrl, finishPresetBootstrap],
  );

  const selectPreset = React.useCallback(
    (preset) => {
      applyPreset(preset, true);
    },
    [applyPreset],
  );

  React.useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!urlStateKey) {
      return;
    }
    if (skipExternalSyncRef.current) {
      skipExternalSyncRef.current = false;
      return;
    }

    if (hasManualGridParams(searchParams, urlStateKey)) {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      suppressManualUrlRef.current = true;
      presetBootstrapRef.current = true;
      setActivePresetId(null);
      const parsed = parseFromUrl();
      const nextBuilt = buildStateFromParsed(parsed);
      setFilterModel(nextBuilt.filterModel);
      setSortModel(nextBuilt.sortModel);
      setPaginationModel(nextBuilt.paginationModel);
      setColumnVisibilityModel(nextBuilt.columnVisibilityModel);
      finishPresetBootstrap();
      return;
    }

    const presetIdFromUrl = getPresetIdFromUrl(searchParams, urlStateKey);
    if (presetIdFromUrl && presetsEnabled && presetsBinding?.loaded) {
      const preset = presetsBinding.presets.find((item) => String(item._id) === presetIdFromUrl);
      if (preset) {
        applyPreset(preset, true);
        return;
      }
      writePresetUrl(null);
      setActivePresetId(null);
      presetBootstrapRef.current = true;
    }
  }, [
    urlStateKey,
    searchParams,
    parseFromUrl,
    buildStateFromParsed,
    presetsEnabled,
    presetsBinding?.loaded,
    presetsBinding?.presets,
    applyPreset,
    writePresetUrl,
    finishPresetBootstrap,
  ]);

  React.useEffect(() => {
    if (!urlStateKey || !presetsEnabled || !presetsBinding?.loaded || presetBootstrapRef.current) {
      return;
    }
    if (hasManualGridParams(searchParams, urlStateKey)) {
      return;
    }
    if (getPresetIdFromUrl(searchParams, urlStateKey)) {
      return;
    }

    const defaultPreset = presetsBinding.presets.find((item) => item.isDefault);
    if (defaultPreset) {
      applyPreset(defaultPreset, true);
      presetBootstrapRef.current = true;
    }
  }, [urlStateKey, presetsEnabled, presetsBinding?.loaded, presetsBinding?.presets, searchParams, applyPreset]);

  const onFilterModelChange = React.useCallback(
    (model) => {
      setFilterModel(model);
      if (shouldTreatGridChangeAsManual()) {
        scheduleManualUrlUpdate({ ...stateRef.current, filterModel: model });
      }
      onFilterModelChangeProp?.(model);
    },
    [shouldTreatGridChangeAsManual, scheduleManualUrlUpdate, onFilterModelChangeProp],
  );

  const onSortModelChange = React.useCallback(
    (model) => {
      setSortModel(model);
      if (shouldTreatGridChangeAsManual()) {
        scheduleManualUrlUpdate({ ...stateRef.current, sortModel: model });
      }
      onSortModelChangeProp?.(model);
    },
    [shouldTreatGridChangeAsManual, scheduleManualUrlUpdate, onSortModelChangeProp],
  );

  const onPaginationModelChange = React.useCallback(
    (model) => {
      setPaginationModel(model);
      if (!shouldTreatGridChangeAsManual()) {
        onPaginationModelChangeProp?.(model);
        return;
      }
      const previous = stateRef.current.paginationModel;
      const pageSizeChanged = model.pageSize !== previous.pageSize;
      const inManualMode = activePresetIdRef.current === null;
      if (pageSizeChanged || inManualMode) {
        scheduleManualUrlUpdate({ ...stateRef.current, paginationModel: model });
      }
      onPaginationModelChangeProp?.(model);
    },
    [shouldTreatGridChangeAsManual, scheduleManualUrlUpdate, onPaginationModelChangeProp],
  );

  const onColumnVisibilityModelChange = React.useCallback(
    (model) => {
      setColumnVisibilityModel(model);
      if (shouldTreatGridChangeAsManual()) {
        scheduleManualUrlUpdate({ ...stateRef.current, columnVisibilityModel: model });
      }
      onColumnVisibilityModelChangeProp?.(model);
    },
    [shouldTreatGridChangeAsManual, scheduleManualUrlUpdate, onColumnVisibilityModelChangeProp],
  );

  const getCurrentPresetState = React.useCallback(
    () => extractPresetStateFromGrid(stateRef.current, { allowedFields: columnFields }),
    [columnFields],
  );

  const clearActivePreset = React.useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const emptyBuilt = buildStateFromParsed(createEmptyParsed(defaultPageSize));
    suppressManualUrlRef.current = true;
    presetBootstrapRef.current = true;
    setFilterModel(emptyBuilt.filterModel);
    setSortModel(emptyBuilt.sortModel);
    setPaginationModel(emptyBuilt.paginationModel);
    setColumnVisibilityModel(emptyBuilt.columnVisibilityModel);
    setActivePresetId(null);
    writePresetUrl(null);
    finishPresetBootstrap();
  }, [defaultPageSize, buildStateFromParsed, writePresetUrl, finishPresetBootstrap]);

  if (!urlStateKey) {
    return { enabled: false };
  }

  return {
    enabled: true,
    filterModel,
    sortModel,
    paginationModel,
    columnVisibilityModel,
    onFilterModelChange,
    onSortModelChange,
    onPaginationModelChange,
    onColumnVisibilityModelChange,
    activePresetId,
    selectPreset,
    clearActivePreset,
    getCurrentPresetState,
  };
}
